import { ScrollStory, StageCanvas, StageCard, StagePanels, type StoryBeat } from '../../../../components/ScrollStory';

/**
 * "How it works" for Inbox Placement — the real path one test travels: create → send → poll →
 * per-ESP placement → composite 0–100 → trend + webhooks. Grounded in the inbox-placement code
 * (InboxPlacementService.create/refreshFromElv/computeDeliverabilityScore/getTrend, seed-sender.ts).
 * The engine underneath is EmailListVerify: it owns the seed mailboxes and the folder detection;
 * SieveGuard orchestrates, sends (auto mode), scores, trends and fires the webhooks. Copy stays
 * honest about that split — no claim that SieveGuard reads the mailboxes itself.
 */

const STAGES = ['Start', 'Send', 'Detect', 'Score', 'Trend', 'Result'];

type Beat = StoryBeat & { detail: React.ReactNode };

const BEATS: Beat[] = [
  {
    n: 1,
    kicker: 'Start',
    title: 'A test gets its seed list',
    body: (
      <>
        You start a test from your real sender. Our provider mints the seed mailboxes and a tracking code
        (<span className="font-mono">ELV-…</span>); in auto mode we send through your stored SMTP, in manual mode you
        fire the list from your own ESP with that code in the body.
      </>
    ),
    facts: ['real sender', 'seed list + code', 'auto or manual'],
    detail: (
      <StageCard head="Create a test">
        <div className="font-mono text-[11px] md:text-[12.5px]">
          <div className="text-faint">POST /v1/inbox-placement</div>
          <div className="text-verify">→ seedEmails[] · code ELV-A1B2C3</div>
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">
          100 credits · auto-send validates SMTP first
        </div>
      </StageCard>
    ),
  },
  {
    n: 2,
    kicker: 'Send',
    title: 'One message per seed',
    body: (
      <>
        In auto mode we dispatch through nodemailer — <b className="font-semibold text-txt">one message per seed</b> so a
        single bad recipient can&rsquo;t abort the batch — with the tracking code appended as{' '}
        <span className="font-mono">Ref: &lt;code&gt;</span>. Seeds span five ESP buckets: Google, Yahoo, Outlook, Zoho
        and Other.
      </>
    ),
    facts: ['per-seed send', 'Ref: <code>', 'google · yahoo · outlook · zoho · other'],
    detail: (
      <StageCard head="Dispatch to seeds">
        <div className="flex flex-wrap gap-1.5 font-mono text-[10.5px] md:text-[11.5px]">
          {['google', 'yahoo', 'outlook', 'zoho', 'other'].map((esp) => (
            <span key={esp} className="rounded border border-border px-1.5 py-0.5 text-muted">
              {esp}
            </span>
          ))}
        </div>
        <div className="mt-2 font-mono text-[10.5px] text-faint md:text-[11px]">
          per-recipient failures collected, never thrown
        </div>
      </StageCard>
    ),
  },
  {
    n: 3,
    kicker: 'Detect',
    title: 'Where did each one land?',
    body: (
      <>
        The engine reads its own seed mailboxes and classifies every seed — Inbox, Promotions (its Category tab), Spam,
        Waiting or Missing. We poll for the result at most every 20 seconds; a test that never completes flips to expired
        after 24 hours.
      </>
    ),
    facts: ['inbox · promo · spam · missing', 'poll ≥ 20s', 'expire at 24h'],
    detail: (
      <StageCard head="Per-ESP placement">
        <div className="grid grid-cols-1 gap-y-1 font-mono text-[11px] md:text-[12.5px]">
          <PlaceRow esp="google" inbox="92%" tone="text-allow" />
          <PlaceRow esp="outlook" inbox="34%" tone="text-review" />
          <PlaceRow esp="yahoo" inbox="88%" tone="text-allow" />
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">Outlook is dragging the average</div>
      </StageCard>
    ),
  },
  {
    n: 4,
    kicker: 'Score',
    title: 'One deliverability number',
    body: (
      <>
        Placements roll into a 0–100 score:{' '}
        <span className="font-mono">100 − spam·1 − missing·0.7 − promo·0.2 − waiting·0.1 + inbox·0.3</span>. Spam and
        missing hurt most; clean inbox landings earn a little back. It maps to a band — excellent / good / mixed / poor.
      </>
    ),
    facts: ['weighted 0–100', 'spam · missing hurt most', '85 · 65 · 40 bands'],
    detail: (
      <StageCard head="computeDeliverabilityScore">
        <div className="font-mono text-[11.5px] md:text-[12.5px]">
          <ScoreRow label="inbox landings" pts="+" tone="text-allow" />
          <ScoreRow label="spam ×1.0 · missing ×0.7" pts="−" tone="text-review" />
          <div className="mt-1 flex items-center justify-between border-t border-bordersoft pt-1">
            <span className="font-semibold">score 71</span>
            <span className="rounded-full bg-verify/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-verify">
              good
            </span>
          </div>
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">bands: 85 excellent · 65 good · 40 mixed</div>
      </StageCard>
    ),
  },
  {
    n: 5,
    kicker: 'Trend',
    title: 'Improving or regressing?',
    body: (
      <>
        Every completed test rolls into a per-sender 30 / 60 / 90-day curve. We split the window in half and compare the
        averages — a delta of 3+ points sets the arrow up or down, so you see direction, not just a single noisy
        snapshot.
      </>
    ),
    facts: ['per-sender curve', 'split-half delta', 'weekly buckets'],
    detail: (
      <StageCard head="30-day reputation trend">
        <div className="flex items-end gap-1.5">
          {[52, 58, 55, 64, 68, 71].map((h, i) => (
            <div
              key={i}
              className="w-4 rounded-t bg-gradient-to-t from-verify/30 to-verify"
              style={{ height: `${h * 0.5}px` }}
            />
          ))}
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-allow md:text-[11px]">direction: up · +9 over the window</div>
      </StageCard>
    ),
  },
  {
    n: 6,
    kicker: 'Result',
    title: 'The verdict, and an alert',
    body: (
      <>
        On completion <span className="font-mono">inbox_placement.test_completed</span> fires with the full summary; if
        the trend turns down by 5+ points, <span className="font-mono">inbox_placement.reputation_regressed</span>{' '}
        follows. The detail page shows the score, the per-ESP split, and the sender&rsquo;s domain-health snapshot.
      </>
    ),
    facts: ['test_completed', 'reputation_regressed', 'joined domain health'],
    detail: (
      <StageCard head="Webhook — test_completed">
        <pre className="overflow-x-auto font-mono text-[10.5px] leading-relaxed text-txt md:text-[12px]">
          <code>{`{ "event": "inbox_placement.test_completed",
  "senderDomain": "acme.co",
  "summary": { "inbox": 71, "promotions": 12,
    "spam": 14, "missing": 3 } }`}</code>
        </pre>
      </StageCard>
    ),
  },
];

export function InboxPlacementStory() {
  const panels = BEATS.map((b) => ({ n: b.n, content: b.detail }));
  return (
    <section className="mt-20" aria-label="How inbox placement works">
      <div className="max-w-2xl">
        <span className="inline-flex items-center gap-2 rounded-full border border-border bg-panel px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-verify">
          <span className="h-1.5 w-1.5 rounded-full bg-verify" /> How it works
        </span>
        <h2 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight md:text-4xl">
          From a real send to a <span className="font-mono text-verify">per-ESP</span> verdict
        </h2>
        <p className="mt-3 text-[14.5px] leading-relaxed text-muted">
          Follow one test from your production sender — real mail to real seed mailboxes, classified per ESP, scored
          0–100, and rolled into a reputation trend you can watch move.
        </p>
      </div>

      <ScrollStory
        beats={BEATS}
        className="mt-6"
        renderStage={(beat, p) => (
          <StageCanvas>
            <div className="absolute inset-x-4 top-4 md:inset-x-6 md:top-6">
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.12em] text-faint md:text-[11px]">
                <span>Inbox placement pipeline</span>
                <span>step {beat}/6</span>
              </div>
              <div className="mt-2 h-1 overflow-hidden rounded-full bg-border">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-verify to-allow transition-all duration-500"
                  style={{ width: `${(beat / 6) * 100}%` }}
                />
              </div>
              <div className="mt-2 font-mono text-[12px] text-verify md:text-[13px]">{STAGES[beat - 1]}</div>
              <span className="sr-only">{`Progress ${Math.round(p * 100)}%`}</span>
            </div>
            <StagePanels beat={beat} panels={panels} />
          </StageCanvas>
        )}
      />
    </section>
  );
}

function PlaceRow({ esp, inbox, tone }: { esp: string; inbox: string; tone: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-faint">{esp}</span>
      <span className={`font-semibold ${tone}`}>{inbox} inbox</span>
    </div>
  );
}

function ScoreRow({ label, pts, tone }: { label: string; pts: string; tone: string }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-muted">{label}</span>
      <span className={tone}>{pts}</span>
    </div>
  );
}
